import React, { useCallback } from 'react'
import PropTypes from 'prop-types'
import { FormGroup } from '@theneatcompany/nui'
import { useController, useFormContext } from 'react-hook-form'

import { Checkbox } from '@/components/common'

import FieldError from './FieldError'

const CheckboxField = ({
  labelProps,
  label = labelProps.label,
  name,
  checked,
  onChange,
  errorProps,
  className,
  disabled,
  containerProps,
  clickableContainer = false,
  ...inputProps
}) => {
  const { control, setValue, formState: { defaultValues, isSubmitting, isSubmitted } } = useFormContext()
  const { field: { value, ...field }, fieldState: { error, isTouched: touched } } = useController({ name, control })
  const initialValue = defaultValues?.[name]
  const handleOnChange = useCallback((e) => {
    const status = e.target.checked
    onChange?.(e)

    // If initial and selected values are equal (after coercion),
    // return the initial value
    // to ensure the form can get back to pristine state
    const updatedValue = (status === !!initialValue) ? initialValue : status
    setValue(name, updatedValue)
  }, [onChange, name, value, setValue, initialValue])

  const handleClick = useCallback(() => {
    setValue(name, !value)
  }, [name, value, setValue])

  const errorMessage = error?.message
  const hasErrors = !isSubmitting && !!(errorMessage && (touched || isSubmitted))

  return (
    <FormGroup
      className={className}
      intent={hasErrors ? 'danger' : undefined}
      onClick={!clickableContainer || disabled ? undefined : handleClick}
      {...containerProps}
    >
      <Checkbox
        checked={checked || !!value}
        disabled={disabled || isSubmitting}
        label={label}
        name={name}
        onChange={handleOnChange}
        status={hasErrors ? 'error' : undefined}
        {...field}
        {...inputProps}
      />
      <FieldError message={errorMessage} touched={touched} {...errorProps} />
    </FormGroup>
  )
}

CheckboxField.defaultProps = {
  labelProps: {},
  onChange: null,
  checked: false,
  errorProps: {},
  className: '',
  containerProps: {},
  disabled: false,
  clickableContainer: false,
}

CheckboxField.propTypes = {
  labelProps: PropTypes.shape({
    label: PropTypes.node,
  }),
  label: PropTypes.node,
  checked: PropTypes.bool,
  onChange: PropTypes.func,
  errorProps: PropTypes.shape({}),
  className: PropTypes.string,
  name: PropTypes.string.isRequired,
  containerProps: PropTypes.shape({}),
  disabled: PropTypes.bool,
  clickableContainer: PropTypes.bool,
}

export default CheckboxField
